<template>
  <div
    :class="[$style.iframeHost, isIframeLoading && $style.iframeHostLoading]"
  >
    <PardotFormLoadingSkeleton
      v-if="isIframeLoading"
      :class="$style.skeleton"
    />
    <iframe
      v-if="iframeSrc"
      :id="iframeId"
      ref="iframeRef"
      :src="iframeSrc"
      frameborder="0"
      hspace="0"
      allowtransparency="true"
      :title="iframeTitle"
      :class="$style.iframe"
      @load="onIframeLoad"
    />
  </div>
</template>

<script setup lang="ts">
import { v } from 'h3-valibot';
import PardotFormLoadingSkeleton from '~/components/global/lib/PardotFormLoadingSkeleton.vue';
import { useIframeResizer } from '~/composables/useIframeResizer';
import { usePardotIframeSrc } from '~/composables/usePardotIframeSrc';
import { jsonParseBaseSchema } from '~/utils/validation/jsonParseBaseSchema';

interface Props {
  formUrl: string;
  id: string | undefined;
}

const props = defineProps<Props>();

const emits = defineEmits<{
  success: [recipientEmail?: string];
}>();

const logger = useLogger();
const { $textDictionary } = useNuxtApp();
const iframeRef = ref<HTMLIFrameElement>();

const iframeId = computed(() => `pardot-form-iframe-${props.id}`);
const iframeTitle = computed(
  () => $textDictionary.value?.['pardotForm.iframeTitle'] ?? 'Download form'
);

const { iframeSrc, isIframeLoading, onIframeLoad } = usePardotIframeSrc(
  () => new URL(props.formUrl),
  { preload: false }
);

useIframeResizer(iframeRef, iframeId.value);

const successMessageModel = jsonParseBaseSchema(
  v.object({
    isDownload: v.boolean()
  })
);

// Pardot sends the submitted recipient email via postMessage (field `email`).
// It may arrive in the same message as the success signal or in a separate one,
// so we capture it whenever it is present and forward it on success. This lets
// the download conversion be tracked as a Google Ads Enhanced Conversion.
const recipientEmailModel = jsonParseBaseSchema(
  v.object({
    email: v.string()
  })
);

let recipientEmail: string | undefined;

function handlePostMessage(event: MessageEvent) {
  if (!props.formUrl) {
    return;
  }

  let origin;

  try {
    origin = new URL(props.formUrl).origin;
  } catch (e) {
    logger.error('failed to retrieve origin from downloadFormUrl', e);

    return;
  }

  if (
    event.origin !== origin ||
    (typeof event.data === 'string' && event.data.includes('iFrameSizer'))
  ) {
    return;
  }

  const emailData = v.safeParse(recipientEmailModel, event.data);
  if (emailData.success && emailData.output.email) {
    recipientEmail = emailData.output.email;
  }

  const data = v.safeParse(successMessageModel, event.data);

  if (data.success && data.output.isDownload) {
    emits('success', recipientEmail);
  } else if (!data.success && !emailData.success) {
    logger.error('failed to parse post message from pardot form', data.issues);
  }
}

onMounted(() => {
  window.addEventListener('message', handlePostMessage, false);
});

onUnmounted(() => {
  window.removeEventListener('message', handlePostMessage);
});
</script>

<style module>
.iframeHost {
  position: relative;
  min-height: 700px;
  width: 100%;
}

.iframeHostLoading .iframe {
  opacity: 0;
}

.skeleton {
  position: absolute;
  inset: 0;
  width: 100%;
  min-height: 700px;
}

.iframe {
  width: 100%;
  min-height: 700px;
  overflow: hidden;
  margin: 0;
}
</style>
